import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, mergeProps as _mergeProps, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
    class: "flex flex-col flex-1 min-h-0 rounded-xl bg-white dark:bg-(--colors-background-black-default) shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] dark:shadow-[0px_0px_4px_rgba(255,255,255,0.1)] overflow-hidden",
    "data-testid": "donation-alloc-panel"
};
const _hoisted_2 = { class: "flex items-center justify-between gap-3 px-5 h-14 border-b border-gainsboro dark:border-(--colors-border-base)" };
const _hoisted_3 = { class: "flex items-center gap-2" };
const _hoisted_4 = { class: "text-base font-semibold text-(--colors-text-default)" };
const _hoisted_5 = { class: "flex items-center gap-2" };
const _hoisted_6 = { class: "flex-1 overflow-x-hidden min-h-0" };
const _hoisted_7 = {
    key: 0,
    class: "flex items-center gap-6 px-5 py-3 border-t border-gainsboro dark:border-(--colors-border-base) bg-(--colors-background-white-secondary) dark:bg-white/5 text-sm"
};
const _hoisted_8 = { class: "text-(--colors-text-secondary)" };
const _hoisted_9 = {
    class: "text-(--colors-text-default) ml-1",
    "data-testid": "donation-alloc-total-allocated"
};
const _hoisted_10 = { class: "text-(--colors-text-secondary)" };
const _hoisted_11 = {
    class: "text-(--colors-text-default) ml-1",
    "data-testid": "donation-alloc-total-receipt"
};
const _hoisted_12 = { class: "text-(--colors-text-secondary)" };
const _hoisted_13 = {
    class: "text-(--colors-text-default) ml-1",
    "data-testid": "donation-alloc-total-unallocated"
};
const _hoisted_14 = { class: "border-t border-gainsboro dark:border-(--colors-border-base) px-5 py-3 bg-white dark:bg-(--colors-background-black-default)" };
const _hoisted_15 = {
    class: "flex flex-col gap-5",
    "data-testid": "donation-alloc-add-modal"
};
const _hoisted_16 = {
    class: "flex flex-col gap-5",
    "data-testid": "donation-alloc-edit-modal"
};
import { computed, h, provide, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { AutoComplete, Button, Checkbox, ConfirmDeleteModal, DataTable, FacetedSearchDrawer, Modal, Pager, TableControls, TextInput, Tooltip, useColumnSettings, useTableQuery, DEFAULT_TABLE_LABELS, TABLE_QUERY_KEY, TABLE_PERMISSIONS_KEY, TABLE_LABELS_KEY, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { buildColumnSettingsLabels } from "@/shared/composables/columnSettingsLabels";
import { useTableQueryUrlSync } from "@/shared/composables/useTableQueryUrlSync";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { useAppToast } from "@/shared/composables/useAppToast";
import { logger } from "@/infrastructure/services/LoggerService";
import { useDonationStore } from "../../../stores/useDonationStore";
import { createDonationAllocation, updateDonationAllocation, deleteDonationAllocation, searchProjects, } from "../../../infrastructure/api/donationApi";
const ADD_ALLOCATION_ACTION = "new";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationTabAllocations',
    props: {
        donationId: {},
        allocations: {},
        isFullyAllocated: { type: Boolean, default: false },
        locked: { type: Boolean, default: false },
        loading: { type: Boolean, default: false }
    },
    emits: ["refresh"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        const { formatAmount, parseAmount } = useFinanceFormatters();
        const donationStore = useDonationStore();
        const currency = computed(() => donationStore.currentDonation?.currency);
        const ROW_OPTIONS = [10, 30, 50];
        // ── Table query (search, pagination) ──────────────────────────────────────────
        const tableQuery = useTableQuery({ pageSize: ROW_OPTIONS[0] });
        provide(TABLE_QUERY_KEY, tableQuery);
        const tablePermissions = { canSaveView: false, canShareView: false };
        provide(TABLE_PERMISSIONS_KEY, tablePermissions);
        const labelsComputed = computed(() => ({
            ...DEFAULT_TABLE_LABELS,
            filterButton: t("table.filter"),
            eraseAll: t("table.eraseAll"),
            hideFilters: (n) => t("table.hideFilters", { count: n }),
            showFilters: (n) => t("table.showFilters", { count: n }),
            closeSearch: t("table.closeSearch"),
            removeFilter: (label) => t("table.removeFilter", { label }),
        }));
        provide(TABLE_LABELS_KEY, labelsComputed);
        useTableQueryUrlSync({ tableQuery, syncSearch: true, defaultPageSize: ROW_OPTIONS[0] });
        // ── Client-side search + pagination ───────────────────────────────────────────
        const filteredRows = computed(() => {
            const term = tableQuery.state.search.trim().toLowerCase();
            if (!term)
                return props.allocations.items;
            return props.allocations.items.filter((entry) => [entry.projectName ?? "", entry.projectId, String(entry.allocatedAmount), String(entry.receiptAmount)]
                .join(" ")
                .toLowerCase()
                .includes(term));
        });
        const totalPages = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / tableQuery.state.pageSize)));
        const pagedRows = computed(() => {
            const start = (tableQuery.state.page - 1) * tableQuery.state.pageSize;
            return filteredRows.value.slice(start, start + tableQuery.state.pageSize);
        });
        // ── Column settings ────────────────────────────────────────────────────────────
        const settingsColumns = () => [
            { key: "project", label: t("finance.donations.detail.alloc.colProject"), visible: true, pinned: false },
            { key: "allocatedAmount", label: t("finance.donations.detail.alloc.colAllocatedAmount"), visible: true, pinned: false },
            { key: "receiptAmount", label: t("finance.donations.detail.alloc.colReceiptAmount"), visible: true, pinned: false },
            { key: "percentage", label: t("finance.donations.detail.alloc.colPercentage"), visible: true, pinned: false },
        ];
        const columnSettings = useColumnSettings({
            columns: settingsColumns,
            defaultOrder: ["project", "allocatedAmount", "receiptAmount", "percentage"],
            defaultPinning: { left: ["select"], right: ["actions"] },
            labels: () => buildColumnSettingsLabels(t),
        });
        const columnOrder = computed(() => ["select", ...columnSettings.order.value, "actions"]);
        // ── Columns ───────────────────────────────────────────────────────────────────
        const columns = computed(() => [
            {
                id: "select",
                locked: true,
                draggable: false,
                resizable: false,
                sortable: false,
                width: 40,
                minWidth: 40,
                maxWidth: 40,
                header: ({ table }) => h(Checkbox, {
                    modelValue: table.getIsAllPageRowsSelected(),
                    indeterminate: table.getIsSomePageRowsSelected(),
                    "onUpdate:modelValue": (v) => table.toggleAllPageRowsSelected(!!v),
                    ariaLabel: t("common.selectAll"),
                }),
                cell: ({ row }) => h(Checkbox, {
                    modelValue: row.getIsSelected(),
                    "onUpdate:modelValue": (v) => row.toggleSelected(!!v),
                    ariaLabel: t("common.selectRow"),
                }),
            },
            {
                // Composite display column (renders projectName ?? projectId), not a single accessor on
                // AllocationEntry — so it carries an explicit `id` rather than a `key`.
                id: "project",
                header: t("finance.donations.detail.alloc.colProject"),
                draggable: true,
                cell: ({ row }) => h(RouterLink, {
                    to: { name: "ProjectDetail", params: { projectId: row.original.projectId } },
                    class: "text-(--colors-text-link) hover:underline",
                    "data-testid": `donation-alloc-project-link-${row.original.allocationId}`,
                }, () => row.original.projectName ?? row.original.projectId),
            },
            {
                key: "allocatedAmount",
                header: t("finance.donations.detail.alloc.colAllocatedAmount"),
                draggable: true,
                cell: ({ row }) => h("span", { class: "font-semibold text-(--colors-text-default)" }, formatAmount(row.original.allocatedAmount, currency.value)),
            },
            {
                key: "receiptAmount",
                header: t("finance.donations.detail.alloc.colReceiptAmount"),
                draggable: true,
                cell: ({ row }) => h("span", { class: "text-(--colors-text-default)" }, formatAmount(row.original.receiptAmount, currency.value)),
            },
            {
                key: "percentage",
                header: t("finance.donations.detail.alloc.colPercentage"),
                draggable: true,
                cell: ({ row }) => h("span", { class: "text-(--colors-text-secondary)" }, row.original.percentage != null ? `${row.original.percentage.toFixed(1)} %` : "–"),
            },
            {
                id: "actions",
                locked: true,
                draggable: false,
                resizable: false,
                sortable: false,
                width: 80,
                minWidth: 80,
                maxWidth: 80,
                header: "",
                // An issued tax receipt locks Affectation — the row is read-only, so render no edit/delete
                // affordance (empty children; keep the column so column order/pinning referencing "actions"
                // stays valid).
                cell: ({ row }) => h("div", { class: "flex items-center gap-3 justify-end pr-2" }, props.locked
                    ? []
                    : [
                        h("button", {
                            class: "flex h-7 w-7 items-center justify-center rounded-full text-(--colors-text-secondary) hover:text-(--colors-primary) hover:bg-(--colors-background-white-secondary) dark:hover:bg-white/5 transition-colors cursor-pointer",
                            "aria-label": t("finance.donations.detail.alloc.editAria"),
                            "data-testid": `donation-alloc-row-edit-${row.original.allocationId}`,
                            onClick: () => openEditModal(row.original),
                        }, [h("span", { class: "ri-pencil-line text-base" })]),
                        h("button", {
                            class: "flex h-7 w-7 items-center justify-center rounded-full text-(--colors-text-secondary) hover:text-red-500 hover:bg-(--colors-background-white-secondary) dark:hover:bg-white/5 transition-colors cursor-pointer",
                            "aria-label": t("finance.donations.detail.alloc.deleteAria"),
                            "data-testid": `donation-alloc-row-delete-${row.original.allocationId}`,
                            onClick: () => openDeleteModal(row.original),
                        }, [h("span", { class: "ri-delete-bin-line text-base" })]),
                    ]),
            },
        ]);
        // ── Form state ─────────────────────────────────────────────────────────────────
        const form = reactive({ projectId: "", allocatedAmount: 0, receiptAmount: 0 });
        // OP-5093 core rule (mirrors the BE Update/CreateDonationAllocationCommandValidator):
        // receiptAmount (tax-receipt-eligible) can never exceed the allocated amount.
        const receiptExceedsAllocated = computed(() => form.receiptAmount > form.allocatedAmount);
        // Mirrors the BE handler rule (Create/ReplaceDonationAllocationsCommandHandler): the sum of
        // allocated amounts can never exceed the donation total. We block the submit client-side so
        // the user gets an inline message instead of an opaque 400 "DomainRuleViolation" toast.
        // When editing, the row being edited already counts towards totalUnallocated, so its current
        // amount is freed back into the available budget.
        const availableToAllocate = computed(() => {
            const editingCurrent = editingAllocationId.value
                ? props.allocations.items.find((a) => a.allocationId === editingAllocationId.value)
                    ?.allocatedAmount ?? 0
                : 0;
            return props.allocations.totalUnallocated + editingCurrent;
        });
        const exceedsUnallocated = computed(() => form.allocatedAmount > availableToAllocate.value);
        const allocatedAmountError = computed(() => form.allocatedAmount > 0 && exceedsUnallocated.value
            ? t("finance.donations.detail.alloc.exceedsUnallocated", {
                amount: formatAmount(availableToAllocate.value, currency.value),
            })
            : null);
        const receiptAmountError = computed(() => receiptExceedsAllocated.value
            ? t("finance.donations.detail.alloc.receiptExceedsAllocated")
            : null);
        const isFormValid = computed(() => form.projectId.trim().length > 0 &&
            form.allocatedAmount > 0 &&
            form.receiptAmount >= 0 &&
            !receiptExceedsAllocated.value &&
            !exceedsUnallocated.value);
        function resetForm() {
            form.projectId = "";
            form.allocatedAmount = 0;
            form.receiptAmount = 0;
            projectOptions.value = [];
        }
        // ── Project picker (oproj-backed autocomplete) ───────────────────────────────────
        // The user searches/selects a project instead of typing its GUID. AutoComplete debounces
        // the @search emit; we fetch from the BFF (→ OPROJ) and store the selected projectId as
        // form.projectId. A monotonic token prevents a slow earlier search overwriting a newer one.
        const projectOptions = ref([]);
        const projectSearchLoading = ref(false);
        let projectSearchSeq = 0;
        async function onProjectSearch(query) {
            const requestId = ++projectSearchSeq;
            projectSearchLoading.value = true;
            try {
                const results = await searchProjects(query.trim());
                if (requestId === projectSearchSeq) {
                    projectOptions.value = results.map((p) => ({
                        value: p.projectId,
                        label: p.name || p.projectId,
                        description: p.code,
                    }));
                }
            }
            catch (err) {
                logger.error("DonationTabAllocations: project search failed", err);
                if (requestId === projectSearchSeq)
                    projectOptions.value = [];
            }
            finally {
                if (requestId === projectSearchSeq)
                    projectSearchLoading.value = false;
            }
        }
        // ── Add modal — URL-synced (?action=new) ─────────────────────────────────────
        // Opening the create-allocation modal reflects in the route so it is deep-linkable,
        // refresh-safe and back-button aware (mirrors the donation create modal's ?action=new
        // on the list). This component only mounts on tab=allocations, so `action=new` is
        // unambiguous here. `replace` keeps history clean, matching the list view's pattern.
        const route = useRoute();
        const router = useRouter();
        const showAddModal = ref(false);
        const isSaving = ref(false);
        function openAddModal() {
            // Never open the create modal when fully allocated or locked by an issued receipt — defends the
            // deep-link (?action=new) and the Resume-tab quick action, not just the now-disabled button.
            if (props.isFullyAllocated || props.locked)
                return;
            resetForm();
            showAddModal.value = true;
            if (route.query.action !== ADD_ALLOCATION_ACTION) {
                router.replace({ query: { ...route.query, action: ADD_ALLOCATION_ACTION } }).catch(() => { });
            }
        }
        function closeAddModal() {
            showAddModal.value = false;
        }
        // Route → modal: open (and reset) when ?action=new appears (click, deep link, or
        // forward nav); close when it is removed (back button).
        watch(() => route.query.action, (action) => {
            if (action === ADD_ALLOCATION_ACTION) {
                // A fully-allocated or receipt-locked donation can't accept a new allocation, so a
                // stale/deep ?action=new must not surface the modal — drop the param.
                if (props.isFullyAllocated || props.locked) {
                    const { action: _action, ...rest } = route.query;
                    router.replace({ query: rest }).catch(() => { });
                    return;
                }
                if (!showAddModal.value) {
                    resetForm();
                    showAddModal.value = true;
                }
            }
            else if (showAddModal.value) {
                showAddModal.value = false;
            }
        }, { immediate: true });
        // Modal → route: drop ?action=new whenever the modal closes by any means (cancel,
        // X, overlay click, successful create) so the URL never lingers on a closed modal.
        watch(showAddModal, (open) => {
            if (!open && route.query.action === ADD_ALLOCATION_ACTION) {
                const { action: _action, ...rest } = route.query;
                router.replace({ query: rest }).catch(() => { });
            }
        });
        async function handleCreate() {
            isSaving.value = true;
            try {
                await createDonationAllocation(props.donationId, {
                    projectId: form.projectId.trim(),
                    allocatedAmount: form.allocatedAmount,
                    receiptAmount: form.receiptAmount,
                });
                closeAddModal();
                emit("refresh");
                toastSuccess("finance.donations.detail.alloc.createSuccess");
            }
            catch {
                toastError("finance.donations.detail.alloc.createError");
            }
            finally {
                isSaving.value = false;
            }
        }
        // ── Edit modal ─────────────────────────────────────────────────────────────────
        const showEditModal = ref(false);
        const editingAllocationId = ref(null);
        function openEditModal(entry) {
            // Defense-in-depth — an issued receipt removes the row edit affordance; ignore any residual
            // call so a locked allocation can't be edited.
            if (props.locked)
                return;
            editingAllocationId.value = entry.allocationId;
            form.projectId = entry.projectId;
            form.allocatedAmount = entry.allocatedAmount;
            form.receiptAmount = entry.receiptAmount;
            // Seed the picker with the current project so the field shows its name (AutoComplete
            // renders the matching option's label for the bound modelValue).
            projectOptions.value = [
                { value: entry.projectId, label: entry.projectName ?? entry.projectId, description: "" },
            ];
            showEditModal.value = true;
        }
        function closeEditModal() {
            showEditModal.value = false;
            editingAllocationId.value = null;
        }
        async function handleUpdate() {
            if (!editingAllocationId.value)
                return;
            isSaving.value = true;
            try {
                await updateDonationAllocation(props.donationId, editingAllocationId.value, {
                    projectId: form.projectId.trim(),
                    allocatedAmount: form.allocatedAmount,
                    receiptAmount: form.receiptAmount,
                });
                closeEditModal();
                emit("refresh");
                toastSuccess("finance.donations.detail.alloc.updateSuccess");
            }
            catch (err) {
                logger.error("DonationTabAllocations: update failed", err);
                toastError("finance.donations.detail.alloc.updateError");
            }
            finally {
                isSaving.value = false;
            }
        }
        // ── Delete modal ───────────────────────────────────────────────────────────────
        const showDeleteModal = ref(false);
        const deletingAllocationId = ref(null);
        const isDeleting = ref(false);
        function openDeleteModal(entry) {
            // Defense-in-depth — an issued receipt removes the row delete affordance; ignore any residual
            // call so a locked allocation can't be deleted.
            if (props.locked)
                return;
            deletingAllocationId.value = entry.allocationId;
            showDeleteModal.value = true;
        }
        async function handleDelete() {
            if (!deletingAllocationId.value)
                return;
            isDeleting.value = true;
            try {
                await deleteDonationAllocation(props.donationId, deletingAllocationId.value);
                showDeleteModal.value = false;
                deletingAllocationId.value = null;
                emit("refresh");
                toastSuccess("finance.donations.detail.alloc.deleteSuccess");
            }
            catch (err) {
                logger.error("DonationTabAllocations: delete failed", err);
                toastError("finance.donations.detail.alloc.deleteError");
            }
            finally {
                isDeleting.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _cache[10] || (_cache[10] = _createElementVNode("span", { class: "ri-pie-chart-2-line text-lg text-(--colors-text-secondary)" }, null, -1 /* CACHED */)),
                            _createElementVNode("h2", _hoisted_4, _toDisplayString(_unref(t)("finance.donations.detail.alloc.title")), 1 /* TEXT */)
                        ]),
                        _createElementVNode("div", _hoisted_5, [
                            (__props.locked || __props.isFullyAllocated)
                                ? (_openBlock(), _createBlock(_unref(Tooltip), {
                                    key: 0,
                                    text: __props.locked
                                        ? _unref(t)('finance.donations.taxReceipt.lockedByReceipt')
                                        : _unref(t)('finance.donations.detail.alloc.fullyAllocatedTooltip'),
                                    position: "bottom",
                                    tabindex: 0
                                }, {
                                    default: _withCtx(() => [
                                        _createVNode(_unref(Button), {
                                            variant: "secondary",
                                            size: "sm",
                                            "icon-left": "ri-add-line",
                                            disabled: "",
                                            label: _unref(t)('finance.donations.detail.addAllocationBtn'),
                                            "data-testid": "donation-alloc-add-button"
                                        }, null, 8 /* PROPS */, ["label"])
                                    ]),
                                    _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["text"]))
                                : (_openBlock(), _createBlock(_unref(Button), {
                                    key: 1,
                                    variant: "secondary",
                                    size: "sm",
                                    "icon-left": "ri-add-line",
                                    label: _unref(t)('finance.donations.detail.addAllocationBtn'),
                                    "data-testid": "donation-alloc-add-button",
                                    onClick: openAddModal
                                }, null, 8 /* PROPS */, ["label"]))
                        ])
                    ]),
                    _createVNode(_unref(TableControls), { "show-filter": "" }),
                    _createElementVNode("div", _hoisted_6, [
                        _createVNode(_unref(DataTable), {
                            columns: columns.value,
                            data: pagedRows.value,
                            "column-visibility": _unref(columnSettings).visibility,
                            "column-order": columnOrder.value,
                            "column-pinning": _unref(columnSettings).pinning.value,
                            loading: __props.loading,
                            "has-settings": "",
                            "sticky-header": "",
                            "empty-text": _unref(t)('finance.donations.detail.alloc.empty'),
                            "settings-aria-label": _unref(t)('columnSettings.title'),
                            onSettingsClick: _unref(columnSettings).openDrawer,
                            "onUpdate:columnOrder": _unref(columnSettings).setOrder,
                            "onUpdate:columnPinning": _unref(columnSettings).setPinning
                        }, null, 8 /* PROPS */, ["columns", "data", "column-visibility", "column-order", "column-pinning", "loading", "empty-text", "settings-aria-label", "onSettingsClick", "onUpdate:columnOrder", "onUpdate:columnPinning"])
                    ]),
                    (props.allocations.items.length > 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                            _createElementVNode("span", _hoisted_8, [
                                _createTextVNode(_toDisplayString(_unref(t)("finance.donations.detail.alloc.totalAllocated")) + " ", 1 /* TEXT */),
                                _createElementVNode("strong", _hoisted_9, _toDisplayString(_unref(formatAmount)(props.allocations.totalAllocated, currency.value)), 1 /* TEXT */)
                            ]),
                            _createElementVNode("span", _hoisted_10, [
                                _createTextVNode(_toDisplayString(_unref(t)("finance.donations.detail.alloc.totalReceipt")) + " ", 1 /* TEXT */),
                                _createElementVNode("strong", _hoisted_11, _toDisplayString(_unref(formatAmount)(props.allocations.totalReceipt, currency.value)), 1 /* TEXT */)
                            ]),
                            _createElementVNode("span", _hoisted_12, [
                                _createTextVNode(_toDisplayString(_unref(t)("finance.donations.detail.alloc.totalUnallocated")) + " ", 1 /* TEXT */),
                                _createElementVNode("strong", _hoisted_13, _toDisplayString(_unref(formatAmount)(props.allocations.totalUnallocated, currency.value)), 1 /* TEXT */)
                            ])
                        ]))
                        : _createCommentVNode("v-if", true),
                    _createElementVNode("div", _hoisted_14, [
                        _createVNode(_unref(Pager), {
                            "current-page": _unref(tableQuery).state.page,
                            "total-pages": totalPages.value,
                            "total-items": filteredRows.value.length,
                            "items-per-page": _unref(tableQuery).state.pageSize,
                            "rows-per-page-options": ROW_OPTIONS,
                            "rows-per-page-label": _unref(t)('pagination.rowsPerPage'),
                            "items-count-separator": _unref(t)('pagination.of'),
                            "previous-label": _unref(t)('pagination.previous'),
                            "next-label": _unref(t)('pagination.next'),
                            "go-to-page-label": _unref(t)('pagination.goToPage'),
                            "go-label": _unref(t)('pagination.go'),
                            "show-go-to-page": "",
                            onPageChange: _unref(tableQuery).setPage,
                            onItemsPerPageChange: _unref(tableQuery).setPageSize
                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "rows-per-page-label", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label", "onPageChange", "onItemsPerPageChange"])
                    ])
                ]),
                _createVNode(_unref(FacetedSearchDrawer), {
                    modelValue: _unref(columnSettings).drawerOpen.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((_unref(columnSettings).drawerOpen.value) = $event)),
                    columns: _unref(columnSettings).settingsItems.value,
                    "default-columns": _unref(columnSettings).defaultSettingsItems.value,
                    "column-order": _unref(columnSettings).order.value,
                    labels: _unref(columnSettings).labels.value,
                    onSave: _unref(columnSettings).handleSave
                }, null, 8 /* PROPS */, ["modelValue", "columns", "default-columns", "column-order", "labels", "onSave"]),
                _createVNode(_unref(Modal), {
                    modelValue: showAddModal.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((showAddModal).value = $event)),
                    size: "sm",
                    "show-close-button": true,
                    "footer-position": "split"
                }, {
                    header: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("finance.donations.detail.alloc.addTitle")), 1 /* TEXT */)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _unref(t)('finance.donations.detail.cancel'),
                            "data-testid": "donation-alloc-add-cancel",
                            onClick: closeAddModal
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _unref(t)('finance.donations.detail.alloc.validate'),
                            loading: isSaving.value,
                            disabled: !isFormValid.value,
                            "data-testid": "donation-alloc-add-validate",
                            onClick: handleCreate
                        }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_15, [
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                modelValue: form.projectId,
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.projectId) = $event)),
                                label: _unref(t)('finance.donations.detail.alloc.projectId'),
                                placeholder: _unref(t)('finance.donations.detail.alloc.projectSearchPlaceholder'),
                                options: projectOptions.value,
                                loading: projectSearchLoading.value,
                                "empty-label": _unref(t)('finance.donations.detail.alloc.projectSearchEmpty'),
                                "icon-left": '',
                                "icon-right": "ri-search-line",
                                "data-testid": "donation-alloc-add-project",
                                "full-width": "",
                                onSearch: onProjectSearch
                            }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "empty-label"]),
                            _createVNode(_unref(TextInput), {
                                "model-value": form.allocatedAmount > 0 ? String(form.allocatedAmount) : '',
                                label: _unref(t)('finance.donations.detail.alloc.allocatedAmount'),
                                placeholder: _unref(formatAmount)(0, currency.value),
                                error: allocatedAmountError.value || undefined,
                                "data-testid": "donation-alloc-add-allocated",
                                "full-width": "",
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = (v) => { const n = _unref(parseAmount)(v); form.allocatedAmount = !isNaN(n) ? n : 0; })
                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "error"]),
                            _createVNode(_unref(TextInput), {
                                "model-value": form.receiptAmount > 0 ? String(form.receiptAmount) : '',
                                label: _unref(t)('finance.donations.detail.alloc.receiptAmount'),
                                placeholder: _unref(formatAmount)(0, currency.value),
                                error: receiptAmountError.value || undefined,
                                "data-testid": "donation-alloc-add-receipt",
                                "full-width": "",
                                "onUpdate:modelValue": _cache[3] || (_cache[3] = (v) => { const n = _unref(parseAmount)(v); form.receiptAmount = !isNaN(n) ? n : 0; })
                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "error"])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue"]),
                _createVNode(_unref(Modal), {
                    modelValue: showEditModal.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((showEditModal).value = $event)),
                    size: "sm",
                    "show-close-button": true,
                    "footer-position": "split"
                }, {
                    header: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("finance.donations.detail.alloc.editTitle")), 1 /* TEXT */)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _unref(t)('finance.donations.detail.cancel'),
                            "data-testid": "donation-alloc-edit-cancel",
                            onClick: closeEditModal
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            label: _unref(t)('finance.donations.detail.alloc.validate'),
                            loading: isSaving.value,
                            disabled: !isFormValid.value,
                            "data-testid": "donation-alloc-edit-validate",
                            onClick: handleUpdate
                        }, null, 8 /* PROPS */, ["label", "loading", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_16, [
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                modelValue: form.projectId,
                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((form.projectId) = $event)),
                                label: _unref(t)('finance.donations.detail.alloc.projectId'),
                                placeholder: _unref(t)('finance.donations.detail.alloc.projectSearchPlaceholder'),
                                options: projectOptions.value,
                                loading: projectSearchLoading.value,
                                "empty-label": _unref(t)('finance.donations.detail.alloc.projectSearchEmpty'),
                                "icon-left": '',
                                "icon-right": "ri-search-line",
                                "data-testid": "donation-alloc-edit-project",
                                "full-width": "",
                                onSearch: onProjectSearch
                            }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "empty-label"]),
                            _createVNode(_unref(TextInput), {
                                "model-value": form.allocatedAmount > 0 ? String(form.allocatedAmount) : '',
                                label: _unref(t)('finance.donations.detail.alloc.allocatedAmount'),
                                placeholder: _unref(formatAmount)(0, currency.value),
                                error: allocatedAmountError.value || undefined,
                                "data-testid": "donation-alloc-edit-allocated",
                                "full-width": "",
                                "onUpdate:modelValue": _cache[6] || (_cache[6] = (v) => { const n = _unref(parseAmount)(v); form.allocatedAmount = !isNaN(n) ? n : 0; })
                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "error"]),
                            _createVNode(_unref(TextInput), {
                                "model-value": form.receiptAmount > 0 ? String(form.receiptAmount) : '',
                                label: _unref(t)('finance.donations.detail.alloc.receiptAmount'),
                                placeholder: _unref(formatAmount)(0, currency.value),
                                error: receiptAmountError.value || undefined,
                                "data-testid": "donation-alloc-edit-receipt",
                                "full-width": "",
                                "onUpdate:modelValue": _cache[7] || (_cache[7] = (v) => { const n = _unref(parseAmount)(v); form.receiptAmount = !isNaN(n) ? n : 0; })
                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "error"])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue"]),
                _createVNode(_unref(ConfirmDeleteModal), {
                    modelValue: showDeleteModal.value,
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((showDeleteModal).value = $event)),
                    title: _unref(t)('finance.donations.detail.alloc.deleteTitle'),
                    subtitle: _unref(t)('finance.donations.detail.alloc.deleteMessage'),
                    "confirm-label": _unref(t)('finance.donations.detail.alloc.deleteConfirm'),
                    "cancel-label": _unref(t)('finance.donations.detail.cancel'),
                    onConfirm: handleDelete
                }, null, 8 /* PROPS */, ["modelValue", "title", "subtitle", "confirm-label", "cancel-label"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
